import { ArrowUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Emblem } from "./Primitives";
import { SOCIALS } from "../data/content";
import { SOCIAL_ICONS } from "./SocialIcons";

const EXPLORE = [
  { label: "Videos", id: "videos" },
  { label: "Topics", id: "topics" },
  { label: "About", id: "about" },
  { label: "Free Guide", to: "/free-guide" },
  { label: "Subscribe", id: "newsletter" },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (item) => {
    if (item.to) {
      navigate(item.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${item.id}`);
      return;
    }
    document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/8 bg-[#050505] overflow-hidden"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 pt-20 md:pt-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <Emblem size={28} className="text-white" />
              <span className="font-display text-2xl tracking-[0.15em]">
                NOIR <span className="text-crimson">PSYCHE</span>
              </span>
            </div>
            <p className="mt-6 max-w-sm text-white/50 text-sm leading-relaxed">
              A cinematic psychology media brand decoding human nature, behavior,
              and consciousness. The mind is the last frontier.
            </p>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-6">
              Explore
            </p>
            <ul className="space-y-3">
              {EXPLORE.map((l) => (
                <li key={l.label}>
                  <button
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => go(l)}
                    className="link-underline text-white/60 hover:text-white transition-colors text-sm"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-6">
              Follow
            </p>
            <ul className="space-y-3">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <li key={s.key}>
                    <a
                      data-testid={`social-${s.key}`}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit Noir Psyche on ${s.label}`}
                      className="group inline-flex items-center gap-3 text-white/60 hover:text-white transition-colors text-sm"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:border-[var(--noir-crimson)] group-hover:bg-[var(--noir-crimson)]/10">
                        <Icon size={15} className="transition-colors group-hover:text-crimson" />
                      </span>
                      {s.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-8">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/30">
            © {new Date().getFullYear()} NOIR PSYCHE. All rights reserved.
          </p>
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/30">
            See What Others Miss.
          </p>
        </div>
      </div>

      {/* Massive wordmark */}
      <div className="relative select-none pointer-events-none">
        <h2 className="font-display font-light text-center leading-none tracking-tight text-white/[0.04] text-[24vw] -mb-[3vw]">
          PSYCHE
        </h2>
      </div>
    </footer>
  );
}
